/**
 * Phase F — Mishap Corrector results view.
 * Reused by /mishap-corrector Step 4 and /mishap-corrector/results/{analysis_id} print-friendly page.
 * Palette: ivy / brass / bone / crimson (severe alerts). No shadows.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const SEVERITY_STYLES = {
  critical: { badge: "bg-crimson text-bone", label: "Critical" },
  high: { badge: "bg-brass text-ivy", label: "High" },
  medium: { badge: "bg-ivy text-bone", label: "Medium" },
  low: { badge: "bg-bone-dim text-ink-soft border border-stone/40", label: "Low" },
};

function useVerificationRecords(ids) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!ids || ids.length === 0) { setRows([]); return; }
    api.get(`/mishap/verification-records?record_ids=${ids.join(",")}`)
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
  }, [ids && ids.join(",")]);
  return rows;
}

function SourceLinks({ ids }) {
  const rows = useVerificationRecords(ids || []);
  if (!rows.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-3">
      {rows.map((r) => (
        <a key={r.record_id} href={r.source_url} target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-[0.18em] text-brass hover:text-ivy border border-brass/40 px-2 py-1" data-testid={`mishap-source-${r.record_id}`}>
          Source — {r.source_title || r.record_id}
        </a>
      ))}
    </div>
  );
}

export default function MishapResultsView({ analysis, showPrintCTA = false }) {
  const [program, setProgram] = useState(null);
  useEffect(() => {
    if (!analysis?.program_slug) return;
    api.get(`/mishap/programs/${analysis.program_slug}`).then((r) => setProgram(r.data)).catch(() => {});
  }, [analysis?.program_slug]);
  if (!analysis) return null;
  const extra = analysis.extra_semesters_added || 0;
  const onTrack = !!analysis.on_track;

  return (
    <div className="space-y-10" data-testid="mishap-results">
      {program && (
        <div className="border-b border-stone/30 pb-4" data-testid="mishap-program-header">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Analyzed against</div>
          <div className="mt-2 font-serif text-lg text-ink tracking-tight">{program.name}</div>
          <div className="mt-1 flex flex-wrap gap-3">
            {program.catalog_url && <a href={program.catalog_url} target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-[0.18em] text-brass hover:text-ivy border border-brass/40 px-2 py-1" data-testid="mishap-program-catalog-link">Catalog →</a>}
            {program.tracking_sheet_url && <a href={program.tracking_sheet_url} target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-[0.18em] text-brass hover:text-ivy border border-brass/40 px-2 py-1" data-testid="mishap-program-tracking-link">Tracking sheet →</a>}
          </div>
        </div>
      )}
      {/* SUMMARY CARD */}
      <div className={`border ${onTrack ? "border-ivy bg-ivy/5" : extra > 0 ? "border-crimson/60 bg-crimson/5" : "border-brass/60 bg-brass/5"} p-6 lg:p-8`} data-testid="mishap-summary-card">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Result</div>
        <h2 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight text-ink">{onTrack ? `You're on track, ${analysis.first_name}.` : `${analysis.first_name}, you need to make ${analysis.mishaps.length} correction${analysis.mishaps.length === 1 ? "" : "s"}.`}</h2>
        <p className="mt-4 text-sm text-ink-soft leading-relaxed max-w-3xl">{analysis.summary}</p>
        <div className="mt-6 grid sm:grid-cols-3 gap-0 border-t border-l border-stone/40 max-w-2xl">
          <div className="border-r border-b border-stone/40 bg-bone p-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Estimated graduation</div>
            <div className="font-serif text-xl text-ink mt-2 tabular-nums">{analysis.estimated_graduation_term || "—"}</div>
          </div>
          <div className="border-r border-b border-stone/40 bg-bone p-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Extra semesters</div>
            <div className={`font-serif text-xl mt-2 tabular-nums ${extra > 0 ? "text-crimson" : "text-ivy"}`} data-testid="mishap-extra-sem">{extra}</div>
          </div>
          <div className="border-r border-b border-stone/40 bg-bone p-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Analyzer model</div>
            <div className="font-mono text-sm text-ink-soft mt-2">{analysis.model || "—"}</div>
          </div>
        </div>
      </div>

      {/* MISHAPS */}
      <section data-testid="mishap-list">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Mishaps detected</div>
        <h3 className="font-serif text-2xl text-ink mt-2 tracking-tight">What&apos;s wrong — and what to do</h3>
        {analysis.mishaps.length === 0 ? (
          <p className="mt-4 text-sm text-ink-soft">No mishaps detected against the UF Dietetics prerequisites.</p>
        ) : (
          <div className="mt-5 space-y-4">
            {analysis.mishaps.map((m, i) => {
              const sev = SEVERITY_STYLES[m.severity] || SEVERITY_STYLES.medium;
              return (
                <article key={i} className="border border-stone/40 bg-bone p-5 lg:p-6" data-testid={`mishap-card-${i}`}>
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div className={`px-2 py-1 text-[10px] uppercase tracking-[0.22em] font-semibold ${sev.badge}`} data-testid={`mishap-severity-${i}`}>{sev.label}</div>
                    {m.related_course_code && <div className="text-[11px] uppercase tracking-[0.22em] text-brass font-mono">{m.related_course_code}</div>}
                  </div>
                  <h4 className="mt-3 font-serif text-lg text-ink tracking-tight">{m.title}</h4>
                  <p className="mt-2 text-sm text-ink-soft leading-relaxed">{m.detail}</p>
                  {m.adviser_action && (
                    <div className="mt-4 border-l-2 border-ivy pl-4 py-1">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-ivy">Adviser action</div>
                      <p className="mt-1 text-sm text-ink">{m.adviser_action}</p>
                    </div>
                  )}
                  <SourceLinks ids={m.verification_record_ids} />
                </article>
              );
            })}
          </div>
        )}
      </section>

      {/* CORRECTIVE SEQUENCE */}
      {analysis.corrective_sequence && analysis.corrective_sequence.length > 0 && (
        <section data-testid="mishap-sequence">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Corrective sequence</div>
          <h3 className="font-serif text-2xl text-ink mt-2 tracking-tight">Term-by-term plan</h3>
          <div className="mt-5 grid md:grid-cols-2 gap-0 border-t border-l border-stone/40">
            {analysis.corrective_sequence.map((t) => {
              const termCredits = (t.courses || []).reduce((s, c) => s + (c.credits || 0), 0);
              return (
                <div key={t.term_index} className="border-r border-b border-stone/40 bg-bone p-5" data-testid={`mishap-term-${t.term_index}`}>
                  <div className="flex items-baseline justify-between">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-brass">{t.term_label}</div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-stone tabular-nums">{termCredits} cr</div>
                  </div>
                  <ul className="mt-4 space-y-3">
                    {t.courses.map((c) => (
                      <li key={c.course_code} className="border-b border-stone/20 pb-3">
                        <div className="flex items-baseline gap-3">
                          <span className="font-mono text-sm text-ivy font-semibold">{c.course_code}</span>
                          <span className="text-sm text-ink">{c.title}</span>
                          <span className="ml-auto text-[10px] uppercase tracking-[0.18em] text-stone tabular-nums">{c.credits} cr</span>
                        </div>
                        <div className="mt-1 text-xs text-ink-soft italic">{c.rationale}</div>
                      </li>
                    ))}
                  </ul>
                  {t.notes && <div className="mt-3 text-[11px] text-stone italic">{t.notes}</div>}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* BOTTOM CTA — Phase H.5: upgrade for critical/high severity */}
      {analysis.cta_variant === "critical" ? (
        <div className="border border-crimson bg-crimson/5 p-6 lg:p-8" data-testid="mishap-bottom-cta-critical">
          <div className="text-[10px] uppercase tracking-[0.22em] text-crimson font-semibold">Critical path detected</div>
          <h3 className="mt-3 font-serif text-2xl text-ink tracking-tight">Book a 30-minute recovery call — first session complimentary for critical mishaps.</h3>
          <p className="mt-3 text-sm text-ink-soft max-w-2xl leading-relaxed">Your analysis flagged {extra > 0 ? `${extra} extra semester${extra === 1 ? "" : "s"} of coursework and ` : ""}at least one critical prerequisite gap. A Beyond Valencia adviser can build the corrective plan around your actual Valencia term calendar.</p>
          <div className="mt-6">
            <Link to="/transfer-audit?ref=mishap-critical" className="bg-ivy hover:bg-ivy/90 text-bone uppercase tracking-[0.18em] text-xs font-semibold py-4 px-6 inline-block" data-testid="mishap-transfer-audit-cta-critical">Book my recovery call →</Link>
          </div>
        </div>
      ) : (
        <div className="border border-brass/50 bg-brass/10 p-6 lg:p-8 flex items-center justify-between gap-4 flex-wrap" data-testid="mishap-bottom-cta">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Next step</div>
            <h3 className="font-serif text-xl text-ink mt-2 tracking-tight">Turn this into a real plan with a Beyond Valencia adviser.</h3>
          </div>
          <Link to="/transfer-audit" className="bg-ivy hover:bg-ivy/90 text-bone uppercase tracking-[0.18em] text-xs font-semibold py-4 px-6" data-testid="mishap-transfer-audit-cta">Talk to a Beyond Valencia adviser →</Link>
        </div>
      )}

      {showPrintCTA && analysis.analysis_id && (
        <div className="text-[11px] text-stone italic pt-2">
          Share or print this analysis:{" "}
          <a href={`/mishap-corrector/results/${analysis.analysis_id}`} className="text-ivy underline break-all" data-testid="mishap-share-link">/mishap-corrector/results/{analysis.analysis_id}</a>
        </div>
      )}
    </div>
  );
}
